import { Platform, Dimensions } from "react-native";
import Constants from "expo-constants";
import * as Device from "expo-device";

import { useAuth } from "@/hooks/auth";
import { useSession } from "@/hooks/session";
import { ClientInfo } from "@/store/types";
import { logger } from "@/utils/logger";
import { submitFeedback } from "@/utils/serenity-api-client";
import Bowser from "bowser";

/**
 * Interface for feedback submission data
 */
export interface FeedbackSubmission {
  text: string;
  feedbackTypes?: string[];
  sessionId?: string;
  screenName?: string;
  /** Firestore id of a reported conversation turn this feedback is about */
  anchorMessageId?: string;
}

/**
 * Hook for submitting feedback with user context.
 *
 * @returns Object with feedback submission function and authentication status
 */
export const useFeedbackSubmission = () => {
  const { user, getIdToken } = useAuth();
  const { sessionId } = useSession();

  /**
   * Submit user feedback with automatic user context and metadata collection.
   * @param feedback - The feedback data to submit.
   * @throws Error if user is not authenticated or submission fails.
   */
  const submitUserFeedback = async (feedback: FeedbackSubmission) => {
    if (!user) {
      throw new Error("User must be authenticated to submit feedback");
    }

    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error("Failed to get auth token");
    }

    // The client only references the reported turn by id. The server decides
    // how much surrounding context to include and resolves the turns at email
    // time.
    const result = await submitFeedback(idToken, {
      text: feedback.text,
      feedbackTypes: feedback.feedbackTypes ?? [],
      sessionId: sessionId || undefined,
      screenName: feedback.screenName,
      anchorMessageId: feedback.anchorMessageId,
      clientInfo: await getClientInfo(),
      clientVersion: Constants.expoConfig?.version || "unknown"
    });

    if (!result.success) {
      logger.error("Failed to submit feedback:", result.message);
      throw new Error(result.message);
    }
    logger.info("Feedback submitted successfully");
  };

  return {
    submitFeedback: submitUserFeedback,
    canSubmitFeedback: !!user
  };
};

export async function getClientInfo(): Promise<ClientInfo> {
  const intl = Intl.DateTimeFormat().resolvedOptions();
  const timezone = intl.timeZone ?? undefined;
  const locale = intl.locale ?? undefined;

  if (Platform.OS === "web") {
    const ua = navigator.userAgent;
    const parser = Bowser.getParser(ua);
    const osName = parser.getOSName() || undefined;
    const osVersion = parser.getOSVersion() || undefined;
    const browserName = parser.getBrowserName() || undefined;
    const browserVersion = parser.getBrowserVersion() || undefined;

    // Heuristic for device model
    const isMobile = /iPhone|Android|Mobile/i.test(ua);
    const deviceModel = isMobile
      ? parser.getPlatform().type || "Mobile"
      : "Desktop";

    const { width, height } = Dimensions.get("window");

    return {
      platform: "web",
      osName,
      osVersion,
      browserName,
      browserVersion,
      userAgent: ua,
      deviceModel,
      locale,
      timezone,
      viewportWidth: Math.round(width),
      viewportHeight: Math.round(height),
      online:
        typeof navigator !== "undefined" &&
        typeof navigator.onLine === "boolean"
          ? navigator.onLine
          : undefined
    };
  }

  return {
    platform: Platform.OS,
    osName: Device.osName ?? undefined,
    osVersion: Device.osVersion ?? undefined,
    deviceModel: Device.modelName ?? undefined,
    userAgent: Device.deviceName ?? undefined,
    locale,
    timezone
  };
}
